import { motion } from "framer-motion";
import { Plus, Trash2, FileText, Info, Code2, Type, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { useState, useEffect, useCallback } from "react";
import { SchemaProperty } from "@/api/tools/tools.types";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";

interface SchemaState {
    name: string;
    type: SchemaProperty['type'];
    description: string;
    required: boolean;
    itemType?: 'string' | 'number' | 'boolean' | 'object';  // For array types - restricted to 4 types
    enum?: string[];  // For string type only
}

interface FunctionState {
    name: string;
    description: string;
    parameters: SchemaState[];
}

interface SchemaFormProps {
    value: FunctionState;
    onChange: (data: FunctionState) => void;
    isReadOnly?: boolean;
}

export function SchemaForm({ value, onChange, isReadOnly = false }: SchemaFormProps) {
    const [functionData, setFunctionData] = useState<FunctionState>(value);

    // Memoize the update function
    const updateParent = useCallback((newData: FunctionState) => {
        const isEqual = JSON.stringify(newData) === JSON.stringify(value);
        if (!isEqual) {
            onChange(newData);
        }
    }, [value, onChange]);

    // Update local state when value prop changes
    useEffect(() => {
        const isEqual = JSON.stringify(functionData) === JSON.stringify(value);
        if (!isEqual) {
            setFunctionData(value);
        }
    }, [value]);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newName = e.target.value.replace(/\s/g, ''); // Remove any spaces
        const newData = {
            ...functionData,
            name: newName
        };
        setFunctionData(newData);
        updateParent(newData);
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newData = {
            ...functionData,
            description: e.target.value
        };
        setFunctionData(newData);
        updateParent(newData);
    };

    const addParameter = () => {
        const newData = {
            ...functionData,
            parameters: [
                ...functionData.parameters,
                {
                    name: "",
                    type: "string" as SchemaProperty['type'],
                    description: "",
                    required: false,
                    itemType: "string" as 'string' | 'number' | 'boolean' | 'object',  // Default for arrays
                    enum: []  // Empty array for string enums
                }
            ]
        };
        setFunctionData(newData);
        updateParent(newData);
    };

    const removeParameter = (index: number) => {
        const newData = {
            ...functionData,
            parameters: functionData.parameters.filter((_, i) => i !== index)
        };
        setFunctionData(newData);
        updateParent(newData);
    };

    const updateParameter = (index: number, field: keyof SchemaState, value: string | boolean | string[] | 'string' | 'number' | 'boolean' | 'object') => {
        const newData = {
            ...functionData,
            parameters: functionData.parameters.map((param, i) => {
                if (i === index) {
                    return { ...param, [field]: value };
                }
                return param;
            })
        };
        setFunctionData(newData);
        updateParent(newData);
    };

    const typeOptions = [
        { value: "string", label: "String" },
        { value: "number", label: "Number" },
        { value: "boolean", label: "Boolean" },
        { value: "array", label: "Array" },
        { value: "object", label: "Object" }
    ];

    // Array item types - restricted to 4 types only
    const arrayItemTypeOptions = [
        { value: "string", label: "String" },
        { value: "number", label: "Number" },
        { value: "boolean", label: "Boolean" },
        { value: "object", label: "Object" }
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
            className="space-y-6"
        >
            {/* Function Information Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Code2 className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Function Information</CardTitle>
                            <CardDescription>
                                Define your function's name and description
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-2">
                        <Label
                            htmlFor="functionName"
                            className="flex items-center gap-2 text-sm font-medium"
                        >
                            <FileText className="h-4 w-4 text-primary" />
                            Function Name
                        </Label>
                        <Input
                            id="functionName"
                            className="h-9"
                            placeholder="e.g. getUserInfo, lookupAddress, sendEmail"
                            value={functionData.name}
                            onChange={handleNameChange}
                            disabled={isReadOnly}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label
                            htmlFor="description"
                            className="flex items-center gap-2 text-sm font-medium"
                        >
                            <Info className="h-4 w-4 text-primary" />
                            Description
                        </Label>
                        <Textarea
                            id="description"
                            className="min-h-[100px] resize-none"
                            placeholder="Describe what this function does and when it should be called..."
                            value={functionData.description}
                            onChange={handleDescriptionChange}
                            disabled={isReadOnly}
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Parameters Card */}
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Type className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Parameters</CardTitle>
                            <CardDescription>
                                Define the parameters your function accepts
                            </CardDescription>
                        </div>
                        {functionData.parameters.length > 0 && (
                            <Button
                                size="sm"
                                onClick={addParameter}
                                className="gap-2"
                                disabled={isReadOnly}
                            >
                                <Plus className="h-4 w-4" />
                                Add Parameter
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {functionData.parameters.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                                <Type className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-sm font-medium mb-1">No Parameters</h3>
                            <p className="text-sm text-muted-foreground mb-4 max-w-sm">
                                This function doesn't have any parameters yet. Add parameters to define what data your function needs.
                            </p>
                            <Button
                                size="sm"
                                onClick={addParameter}
                                className="gap-2"
                                disabled={isReadOnly}
                            >
                                <Plus className="h-4 w-4" />
                                Add Your First Parameter
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {functionData.parameters.map((param, index) => (
                                <Card key={index} className="shadow-sm">
                                    <CardHeader className="border-b bg-muted/20 py-2.5">
                                        <div className="flex items-center gap-2">
                                            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
                                                <FileText className="h-3.5 w-3.5 text-primary" />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="text-sm font-medium leading-none">Parameter {index + 1}</h4>
                                                {param.name && (
                                                    <p className="text-xs text-muted-foreground/70 mt-0.5">
                                                        {param.name}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1.5">
                                                    <Switch
                                                        id={`required-${index}`}
                                                        checked={param.required}
                                                        onCheckedChange={(checked) => updateParameter(index, 'required', checked)}
                                                        disabled={isReadOnly}
                                                    />
                                                    <Label htmlFor={`required-${index}`} className="text-xs">Required</Label>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => removeParameter(index)}
                                                    className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10 h-7 px-2"
                                                    disabled={isReadOnly}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label htmlFor={`name-${index}`} className="flex items-center gap-2 text-sm font-medium">
                                                    <Code2 className="h-4 w-4 text-primary" />
                                                    Name
                                                </Label>
                                                <Input
                                                    id={`name-${index}`}
                                                    className="h-9"
                                                    value={param.name}
                                                    onChange={(e) => updateParameter(index, 'name', e.target.value)}
                                                    placeholder="e.g. userId, email"
                                                    disabled={isReadOnly}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor={`type-${index}`} className="flex items-center gap-2 text-sm font-medium">
                                                    <Type className="h-4 w-4 text-primary" />
                                                    Type
                                                </Label>
                                                <Combobox
                                                    options={typeOptions}
                                                    value={param.type}
                                                    onValueChange={(value) => updateParameter(index, 'type', value as SchemaProperty['type'])}
                                                    placeholder="Select type..."
                                                    className="w-full"
                                                    disabled={isReadOnly}
                                                />
                                            </div>
                                        </div>
                                        
                                        {/* Show Array Item Type field if type is array */}
                                        {param.type === 'array' && (
                                            <div className="space-y-2">
                                                <Label htmlFor={`itemType-${index}`} className="flex items-center gap-2 text-sm font-medium">
                                                    <Type className="h-4 w-4 text-primary" />
                                                    Array Item Type
                                                </Label>
                                                <Combobox
                                                    options={arrayItemTypeOptions}
                                                    value={param.itemType || 'string'}
                                                    onValueChange={(value) => updateParameter(index, 'itemType', value as 'string' | 'number' | 'boolean' | 'object')}
                                                    placeholder="Select item type..."
                                                    className="w-full"
                                                    disabled={isReadOnly}
                                                />
                                            </div>
                                        )}

                                        {/* Show Enum field if type is string */}
                                        {param.type === 'string' && (
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <Label className="flex items-center gap-2 text-sm font-medium">
                                                        <Type className="h-4 w-4 text-primary" />
                                                        Enum Values (Optional)
                                                    </Label>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => {
                                                            const currentEnum = param.enum || [];
                                                            updateParameter(index, 'enum', [...currentEnum, '']);
                                                        }}
                                                        disabled={isReadOnly}
                                                        className="h-7 gap-1.5"
                                                    >
                                                        <Plus className="h-3.5 w-3.5" />
                                                        Add Enum
                                                    </Button>
                                                </div>
                                                
                                                {(!param.enum || param.enum.length === 0) ? (
                                                    <div className="rounded-lg border border-dashed bg-muted/30 p-4 text-center">
                                                        <p className="text-sm text-muted-foreground">
                                                            No enum values defined. Click "Add Enum" to restrict this string to specific options.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {param.enum.map((enumValue, enumIndex) => (
                                                            <div key={enumIndex} className="flex items-center gap-2">
                                                                <Input
                                                                    className="h-9 flex-1"
                                                                    value={enumValue}
                                                                    onChange={(e) => {
                                                                        const newEnum = [...(param.enum || [])];
                                                                        newEnum[enumIndex] = e.target.value;
                                                                        updateParameter(index, 'enum', newEnum);
                                                                    }}
                                                                    placeholder={`Option ${enumIndex + 1}`}
                                                                    disabled={isReadOnly}
                                                                />
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        const newEnum = (param.enum || []).filter((_, i) => i !== enumIndex);
                                                                        updateParameter(index, 'enum', newEnum);
                                                                    }}
                                                                    disabled={isReadOnly}
                                                                    className="h-9 w-9 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="space-y-2">
                                            <Label htmlFor={`description-${index}`} className="flex items-center gap-2 text-sm font-medium">
                                                <Info className="h-4 w-4 text-primary" />
                                                Description
                                            </Label>
                                            <Textarea
                                                id={`description-${index}`}
                                                className="min-h-[70px] resize-none"
                                                value={param.description}
                                                onChange={(e) => updateParameter(index, 'description', e.target.value)}
                                                placeholder="Describe what this parameter is used for..."
                                                disabled={isReadOnly}
                                            />
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </motion.div>
    );
}
